<template>
    <app-layout>
        <div v-if="!showForm">
            <div class="max-w-7xl m-4 py-10 sm:px-6 lg:px-8 shadow sm:rounded-md bg-white">
                <h2 class="text-xl"><span class='font-bold'>Required Action:</span> Complete the Phone Number Verification Application</h2>
                <br /><br />
                <span class='font-bold'>Why is this happening?</span>
                Telecommunication carriers (Verizon, ATT, etc.) are now requiring phone numbers that send messages from applications, like TextSpot and our competitors, to be verified in order to prevent spam, fraud, and so they know who is sending messages across their networks.
                <br /><br />
                <span class='font-bold'>What do I need to do?</span><br />
                <span class='text-blue-500 cursor-pointer font-bold' @click="toggleForm">Fill out this form</span> to start the verification process. This process is free and is required in order to continue using TextSpot. If you don’t complete the form you will not be able to send messages from TextSpot after July 5th, 2023. Once submitted, we will review your application for errors and then submit the application for you. Once you are verified you will be informed.
                <br /><br />
                <span class='font-bold'>What does this mean for my account?</span><br />
                Once you’ve completed the form, you will still be able to use your current phone number number for a few weeks.
                <br /><br />
                The verification process for local phone numbers is different than the toll-free phone number verification process. To ensure your messages are successfully delivered to your contacts, TextSpot is choosing to switch to toll-free numbers. You will be provided a new toll-free phone number to send and receive messages from TextSpot at a later date. You will be notified before your number changes. No other changes will be made to your account.
                <br /><br />
                Once we switch your TextSpot phone number to a toll-free phone number (before July 5th) you will no longer receive incoming messages from your previous phone number.
                <br /><br />
                <span class='font-bold'>Can I get my current local number verified and keep it?</span><br />
                Yes. However, that is not our recommendation as the verification process (managed by a third-party company, not TextSpot) is currently unreliable and very slow. You would potentially go several weeks without being able to use your local phone number until you are verified. If you would like to try to keep your current number, visit our <Link href="/verified/start" class="text-blue-500 font-bold cursor-pointer">Verified Sender application page</Link>.
                <br /><br />
                <span class='italic'>Thank you for your patience as we manage the constantly evolving A2P (application-to-person) messaging industry changes. If you have any additional questions, please <span class="cursor-pointer text-blue-500 font-bold" @click="openchat">start a chat with our support team</span>.</span>
                <br /><br />

                <!-- google form 
                    <a href="https://forms.gle/Z3myyMV3YjXM4dkp6" target="_blank">
                        <jet-button class="mt-8">
                            Get Started
                        </jet-button>
                    </a>
                -->
                 <jet-button class="mt-8" @click.native="toggleForm">
                    Complete the verification form
                </jet-button>
            </div>
        </div>


        <div class="" v-if="showForm">
            <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8 shadow sm:rounded-md bg-white">
                <jet-form-section @submitted="saveProgress">
                    <template #title>
                        Verification Form
                    </template>
                    <template #form>
                        <div class="col-span-6">
                            <label class="flex flex-col">
                                <div>
                                    Legal Company Name (i.e. include LLC if it is in your legal name)<span class="required">*</span>
                                </div>
                                <jet-input
                                    class="mt-1 block w-full"
                                    v-model="form.legal_business_name"
                                />
                            </label>
                        </div>
                        <div class="col-span-6">
                            <label class="flex flex-col">
                                <div>
                                    Website URL<span class="required">*</span>
                                </div>
                                <jet-input
                                    class="mt-1 block w-full"
                                    v-model="form.website_url"
                                />
                            </label>
                        </div>
                        <div class="col-span-6">
                            <label class="flex flex-col">
                                <div>
                                    Business Address Line 1 (ex: 123 Main St.)<span class="required">*</span>
                                </div>
                                <jet-input
                                    class="mt-1 block w-full"
                                    v-model="form.business_address_1"
                                />
                            </label>
                        </div>
                        <div class="col-span-6">
                            <label class="flex flex-col">
                                <div>
                                    Business Address Line 2 (ex: Suite 5)<span class="required">*</span>
                                </div>
                                <jet-input
                                    class="mt-1 block w-full"
                                    v-model="form.business_address_2"
                                />
                            </label>
                        </div>

                        <div class="col-span-6">
                            <label class="flex flex-col">
                                <div>Country <span class="required">*</span></div>
                                <!--
                                    :options="countryOptions"
                                    v-model="form.country"
                                    :filterable="false"
                                    :clearable="false"
                                    placeholder="Select"
                                    :searchable="false"
                                    :reduce="opt => opt.value"
                            -->
                            </label>
                        </div>
                        
                        <div class="col-span-6">
                            <div class="flex w-full">
                                <jet-input
                                    class="mt-1 mr-2 block w-full"
                                    v-model="form.city"
                                    placeholder="City"
                                    required
                                />

                                <div class="w-80 mt-1">
                                    <state-selector v-if="form.country == 'US'" v-model="form.state" />
                                    <province-selector v-if="form.country == 'CA'" v-model="form.state" />
                                </div>

                                <jet-input
                                    class="mt-1 block w-40 ml-2"
                                    v-model="form.postal_code"
                                    :placeholder="form.country == 'US' ? 'Zip Code' : 'Postal Code'"
                                    required
                                />
                            </div>  
                        </div>

                         <div class="col-span-3">
                            <label class="flex flex-col">
                                <div>
                                    Organization Contact First Name <span class="required">*</span>
                                </div>
                                <jet-input
                                    class="mt-1 mr-2 block w-half"
                                    v-model="form.contact_first_name"
                                    placeholder=""
                                />
                            </label>
                        </div>

                        <div class="col-span-3">
                            <label class="flex flex-col">
                                <div>
                                    Organization Contact Last Name <span class="required">*</span>
                                </div>
                                <jet-input
                                    class="mt-1 mr-2 block w-half"
                                    v-model="form.contact_last_name"
                                    placeholder=""
                                />
                            </label>
                        </div>
                    

                        <div class="col-span-6">
                            <label class="flex flex-col">
                                <div>
                                    Organization Contact Email <span class="required">*</span>
                                </div>
                                <jet-input
                                    class="mt-1 mr-2 block w-full"
                                    v-model="form.contact_email"
                                    placeholder=""
                                />
                            </label>
                        </div>

                        <div class="col-span-6">
                            <label class="flex flex-col">
                                <div>
                                    Organization Contact Phone Number <span class="required">*</span>
                                </div>
                                <jet-input
                                    class="mt-1 mr-2 block w-full"
                                    v-model="form.contact_phone_number"
                                    placeholder=""
                                />
                            </label>
                        </div>

                        <div class="col-span-6">
                            <label class="flex flex-col">
                                <div>Estimated Monthly Message Volume <span class="required">*</span></div>
                                <div class="text-sm text-gray-500">Pick the number closest to what you expect to send.</div>
                                <!--
                                    :options="estimatedVolumeOptions"
                                    v-model="form.message_volume"
                                    :filterable="false"
                                    :clearable="false"
                                    placeholder="Select"
                                    :searchable="false"
                                    :reduce="opt => opt.value"
                            -->
                            </label>
                        </div>

                        <div class="col-span-6">
                            <div class="flex flex-col">
                                <div>What You'll Be Using TextSpot For (select all that apply)</div>
                                
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="2FA" v-model="form.usecases" />
                                    <span class="ml-2">2FA</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="App Notifications" v-model="form.usecases" />
                                    <span class="ml-2">App Notifications</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Appointments" v-model="form.usecases" />
                                    <span class="ml-2">Appointments</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Auctions" v-model="form.usecases" />
                                    <span class="ml-2">Auctions</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Auto Repair Services" v-model="form.usecases" />
                                    <span class="ml-2">Auto Repair Services</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Bank Transfers" v-model="form.usecases" />
                                    <span class="ml-2">Bank Transfers</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Billing" v-model="form.usecases" />
                                    <span class="ml-2">Billing</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Booking Confirmations" v-model="form.usecases" />
                                    <span class="ml-2">Booking Confirmations</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Business Updates" v-model="form.usecases" />
                                    <span class="ml-2">Business Updates</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="COVID-19 Alerts" v-model="form.usecases" />
                                    <span class="ml-2">COVID-19 Alerts</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Career Training" v-model="form.usecases" />
                                    <span class="ml-2">Career Training</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Chatbot" v-model="form.usecases" />
                                    <span class="ml-2">Chatbot</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Conversational / Alerts" v-model="form.usecases" />
                                    <span class="ml-2">Conversational / Alerts</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Courier Services & Deliveries" v-model="form.usecases" />
                                    <span class="ml-2">Courier Services & Deliveries</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Emergency Alerts" v-model="form.usecases" />
                                    <span class="ml-2">Emergency Alerts</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Events & Planning" v-model="form.usecases" />
                                    <span class="ml-2">Events & Planning</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Financial Services" v-model="form.usecases" />
                                    <span class="ml-2">Financial Services</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Fraud Alerts" v-model="form.usecases" />
                                    <span class="ml-2">Fraud Alerts</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Fundraising" v-model="form.usecases" />
                                    <span class="ml-2">Fundraising</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="General Marketing" v-model="form.usecases" />
                                    <span class="ml-2">General Marketing</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="General School Updates" v-model="form.usecases" />
                                    <span class="ml-2">General School Updates</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="HR / Staffing" v-model="form.usecases" />
                                    <span class="ml-2">HR / Staffing</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Healthcare Alerts" v-model="form.usecases" />
                                    <span class="ml-2">Healthcare Alerts</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Housing Community Updates" v-model="form.usecases" />
                                    <span class="ml-2">Housing Community Updates</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Insurance Services" v-model="form.usecases" />
                                    <span class="ml-2">Insurance Services</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Job Dispatch" v-model="form.usecases" />
                                    <span class="ml-2">Job Dispatch</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Legal Services" v-model="form.usecases" />
                                    <span class="ml-2">Legal Services</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Mixed" v-model="form.usecases" />
                                    <span class="ml-2">Mixed</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Motivational Reminders" v-model="form.usecases" />
                                    <span class="ml-2">Motivational Reminders</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Notary Notifications" v-model="form.usecases" />
                                    <span class="ml-2">Notary Notifications</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Order Notifications" v-model="form.usecases" />
                                    <span class="ml-2">Order Notifications</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Political" v-model="form.usecases" />
                                    <span class="ml-2">Political</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Public Works" v-model="form.usecases" />
                                    <span class="ml-2">Public Works</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Real Estate Services" v-model="form.usecases" />
                                    <span class="ml-2">Real Estate Services</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Religious Services" v-model="form.usecases" />
                                    <span class="ml-2">Religious Services</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Repair and Diagnostics Alerts" v-model="form.usecases" />
                                    <span class="ml-2">Repair and Diagnostics Alerts</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Rewards Program" v-model="form.usecases" />
                                    <span class="ml-2">Rewards Program</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Surveys" v-model="form.usecases" />
                                    <span class="ml-2">Surveys</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="System Alerts" v-model="form.usecases" />
                                    <span class="ml-2">System Alerts</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Voting Reminders" v-model="form.usecases" />
                                    <span class="ml-2">Voting Reminders</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Waitlist Alerts" v-model="form.usecases" />
                                    <span class="ml-2">Waitlist Alerts</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Webinar Reminders" v-model="form.usecases" />
                                    <span class="ml-2">Webinar Reminders</span>
                                </label>
                                <label class="inline-flex items-center mt-2">
                                    <input type="checkbox" class="form-checkbox" value="Workshop Alerts" v-model="form.usecases" />
                                    <span class="ml-2">Workshop Alerts</span>
                                </label>
                                
                            </div>
                        </div>

                        <div class="col-span-6 mt-3">
                            <label class="flex flex-col font-normal">
                                <div class="mb-2">
                                    <strong>Use Case Summary <span class="required">*</span></strong><br />

                                    Description of what types of messages you will be sending. The more detail, the better! <br /><br />
                                    <strong>Good Example</strong>: "This number is used to send out promotional offers and coupons to the customers of John’s Coffee Shop."
                                    <br /><br />

                                    <strong>Rejected Example</strong>: "Marketing"
                                    <br />

                                    <div class='italic text-gray-700'>What kind of marketing? What will the end-user/mobile handset be receiving from the end-user business?</div>
                                </div>
                                <jet-input
                                    class="mt-1 mr-2 block w-full"
                                    v-model="form.usecase_summary"
                                    placeholder="Your answer"
                                />
                            </label>
                        </div>

                        <div class="col-span-6 mt-3">
                            <label class="flex flex-col font-normal">
                                <div class="mb-2">
                                    <strong>Message Example <span class="required">*</span></strong><br />

                                    This sample should include your name and opt out language. <br /><br />
                                    <strong>Good Example</strong>: "Thank you for being a loyal customer of John’s Coffee Shop. Enjoy 10% off your next purchase. Reply STOP to opt out."
                                    <br /><br />

                                    <strong>Rejected Example</strong>: "Your appointment is today at 10:00 AM"
                                    <br />

                                </div>
                                <jet-input
                                    class="mt-1 mr-2 block w-full"
                                    v-model="form.message_example"
                                    placeholder="Your answer"
                                />
                            </label>
                        </div>

                        <div class="col-span-6 mt-3">
                            <label class="flex flex-col font-normal">
                                <div class="mb-2">
                                    <strong>How Users Opt-In <span class="required">*</span></strong><br />
                                    <p>
                                        In as much detail, provide how your contacts provide consent to receive your messages. This should be what the mobile user sees when they provide their phone number to subscribe to your messages.

                                        The more detail about the opt in process, the better. The information provided for review should be clear to the Verification Ops team what specifically the customer does to opt in/sign up to receive SMS from the end-user business.

                                    </p> <br /><br />
                                    <strong>Good Example</strong>:  “Keyword Coffee. The keyword is found on a sign at the register of John’s Coffee Shop where customers can see the keyword and text in to the Toll-Free Number.”
                                    <br /><br />

                                    <strong>Good Example</strong>:  "Customers opt in when joining the Donut Reward Club, in-person (physical form) or on our website by explicitly checking a box to agree to SMS"
                                    <br /><br />

                                    <strong>Rejected Example</strong>: "Keyword/Text to join. Online. PointOfSale" <br/>
                                    <div class='italic text-gray-700'>This example does not provide enough information.</div>
                                    <br />
                                </div>
                                
                                <jet-input
                                    class="mt-1 mr-2 block w-full"
                                    v-model="form.how_users_opt_in"
                                    placeholder="Your answer"
                                />
                            </label>
                        </div>

                    </template>
                    <template #actions>
                        <jet-action-message :on="form.recentlySuccessful" class="mr-3">
                            Saved.
                        </jet-action-message>

                        <jet-button
                            :class="{ 'opacity-25': form.processing }"
                            :disabled="!goodToContinue"
                        >
                            Complete
                        </jet-button>
                    </template>
                </jet-form-section>
            
            </div>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from "../../Layouts/AppLayout";
import JetButton from "./../../Jetstream/Button";
import JetFormSection from "./../../Jetstream/FormSection";
import JetInput from "./../../Jetstream/Input";
import JetInputError from "./../../Jetstream/InputError";
import JetLabel from "./../../Jetstream/Label";
import JetActionMessage from "./../../Jetstream/ActionMessage";
import JetSecondaryButton from "./../../Jetstream/SecondaryButton";

import stateSelector from "../../components/Address/StateSelector";
import provinceSelector from "../../components/Address/ProvinceSelector";

export default {
    name: 'A2P-TollFreeVerification',
    props: ["sessions"],
    components: {
        AppLayout,
        JetActionMessage,
        JetButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        
        //vSelect,
        stateSelector,
        provinceSelector
    },
    data() {
        return {
            showForm: false,
        
            form: this.$inertia.form(
                {
                    _method: "POST",
                    country: 'US',
                    usecases: [],
                    legal_business_name: '',
                    website_url: '',
                    business_address_1: '',
                    business_address_2: '',
                    city: '',
                    state: '',
                    postal_code: '',
                    contact_first_name: '',
                    contact_last_name: '',
                    contact_email: '',
                    contact_phone_number: '',
                    message_volume: '',
                    usecase_summary: '',
                    message_example: '',
                    how_users_opt_in: ''
                },
                {
                }
            ),
            countryOptions: [
                {
                    label: 'United States',
                    value: 'US'
                },
                {
                    label: 'Canada',
                    value: 'CA'
                }
            ],
            estimatedVolumeOptions: [
                { label: '10', value: 10 },
                { label: '100', value: 100 },
                { label: '1,000', value: 1000 },
                { label: '10,000', value: 10000 },
                { label: '100,000', value: 100000 },
                { label: '250,000', value: 250000 },
                { label: '500,000', value: 500000 },
                { label: '750,000', value: 750000 },
                { label: '1,000,000', value: 1000000 },
                { label: '5,000,000', value: 5000000 },
                { label: '10,000,000', value: 10000000 }
            ]

        }
    },
    computed: {
        goodToContinue() {
            if (
                this.form.legal_business_name &&
                this.form.website_url &&
                this.form.business_address_1 &&
                this.form.business_address_2 &&
                this.form.country &&
                this.form.city &&
                this.form.state &&
                this.form.postal_code &&
                this.form.contact_first_name &&
                this.form.contact_last_name &&
                this.form.contact_email &&
                this.form.contact_phone_number &&
                this.form.message_volume &&
                this.form.usecase_summary &&
                this.form.message_example &&
                this.form.how_users_opt_in && 
                this.form.usecases.length > 0
            ) {
                return true;
            }

            return false;
        },
    },
    methods: {
        toggleForm() {
            this.showForm = !this.showForm;
        },
        saveProgress() {
            this.form.post("/verified/submit-toll-free-verification", {
                preserveScroll: true,
                onSuccess: (response) => {
                },
            });
        },
        openchat() {
            $crisp.push(['do', 'chat:open'])
        }
        
    }
};
</script>

<style lang="scss" scoped>
.required {
    @apply text-red-500 font-bold text-xl pl-1;
}
</style>